import { withTranslation } from 'react-i18next';
import moment from 'moment';

import { formatPeriod, searchTypeLabel } from '../period';

/**
 * How much of what was asked for actually came back.
 *
 * Requesting final data is not the same as every requested date existing, so the two are reported
 * separately: what was asked for, what was observed, and how many days are unaccounted for. A
 * missing day is shown as unknown rather than folded into the totals as a zero, because that is
 * the difference between a quiet day and a day Search Console has nothing for yet.
 *
 * It reads as a status strip rather than a panel of its own — it qualifies the figures below it,
 * and a reader who trusts the dates should be able to skim past it in one line.
 */
function PeriodCoverage({ t, label, period, coverage, status, error }) {
    return (
        <div className="gsc-tool-coverage-item">
            <strong>{label}</strong>

            {!period && <span className="muted">{t('tools.gsc.coverage.no_baseline')}</span>}

            {!!period && (
                <>
                    <span className="gsc-tool-coverage-range">{formatPeriod(period)}</span>
                    {status === 'loading' && (
                        <span className="muted">{t('tools.gsc.coverage.loading')}</span>
                    )}
                    {status === 'error' && (
                        <span className="gsc-tool-coverage-missing">
                            {error ?? t('global.something_went_wrong')}
                        </span>
                    )}
                    {status === 'done' && !!coverage && (
                        <span
                            className={
                                coverage.missing.length ? 'gsc-tool-coverage-missing' : 'muted'
                            }
                        >
                            {t('tools.gsc.coverage.returned', {
                                observed: coverage.observed.length,
                                requested: coverage.requested.length,
                                missing: coverage.missing.length,
                            })}
                        </span>
                    )}
                </>
            )}
        </div>
    );
}

function CoverageStrip({ t, scope, current, baseline, fetchedAt, statusNote }) {
    const missing =
        (current?.coverage?.missing?.length ?? 0) + (baseline?.coverage?.missing?.length ?? 0);
    const settled = current?.status === 'done';

    return (
        <div className="gsc-tool-coverage">
            <div className="gsc-tool-coverage-state">
                <span
                    className={`gsc-tool-status-dot ${missing ? 'warn' : ''}`}
                    aria-hidden="true"
                />
                <div>
                    <h4>{t(`tools.gsc.data_state.${scope.dataState}`)}</h4>
                    <p>
                        {/* A report that never requests a date dimension has no date coverage to
                            report, and must say what it does cover instead of claiming every
                            requested date came back. */}
                        {!settled
                            ? t('tools.gsc.coverage.loading')
                            : statusNote
                            ? statusNote
                            : missing
                            ? t('tools.gsc.coverage.some_missing', { missing })
                            : t('tools.gsc.coverage.all_present')}
                    </p>
                </div>
            </div>

            <PeriodCoverage
                t={t}
                label={t('tools.gsc.coverage.current')}
                period={scope.current}
                coverage={current?.coverage}
                status={current?.status}
                error={current?.error}
            />

            <PeriodCoverage
                t={t}
                label={t('tools.gsc.coverage.baseline')}
                period={scope.baseline}
                coverage={baseline?.coverage}
                status={baseline?.status}
                error={baseline?.error}
            />

            <div className="gsc-tool-coverage-foot">
                <span>
                    {t('tools.gsc.coverage.source', {
                        type: searchTypeLabel(scope.searchType, t),
                    })}
                </span>
                {!!fetchedAt && (
                    <span>
                        {t('tools.gsc.coverage.fetched', {
                            time: moment(fetchedAt).format('DD MMM YYYY, HH:mm'),
                        })}
                    </span>
                )}
            </div>
        </div>
    );
}

export default withTranslation()(CoverageStrip);
